import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { navLinks } from "@/mocks/home";

export default function Header() {
  const { t, i18n } = useTranslation("home");
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileDropdown, setMobileDropdown] = useState<string | null>(null);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
    setMobileDropdown(null);
    setLangDropdownOpen(false);
  }, [location.pathname]);

  const currentLang = i18n.language === "en" ? "EN" : "ES";

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    setLangDropdownOpen(false);
  };

  const handleMouseEnter = (labelKey: string) => {
    if (dropdownTimerRef.current) clearTimeout(dropdownTimerRef.current);
    setOpenDropdown(labelKey);
  };

  const handleMouseLeave = () => {
    dropdownTimerRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  };

  return (
    <header
      className={`w-full transition-all duration-300 ${
        scrolled
          ? "fixed top-0 left-0 z-50 bg-white shadow-md"
          : "relative z-40 bg-white"
      }`}
    >
      <div className="w-full px-4 md:px-6 flex items-center justify-between py-3 md:py-4">
        <Link to="/" className="flex items-center shrink-0">
          <img
            src="https://static.readdy.ai/image/02db88704cc813a7fe327266af8be0ec/e1552b845480af1b26772bb65895dac3.jpeg"
            alt="Ventra Core"
            className="h-10 md:h-12 w-auto object-contain"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <div key={link.labelKey} className="relative group">
              {link.children ? (
                <div
                  className="relative"
                  onMouseEnter={() => handleMouseEnter(link.labelKey)}
                  onMouseLeave={handleMouseLeave}
                >
                  <button className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-text uppercase tracking-wide hover:text-brandgreen transition-colors whitespace-nowrap cursor-pointer">
                    {t(link.labelKey)}
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-arrow-down-s-line text-sm transition-transform ${openDropdown === link.labelKey ? "rotate-180" : ""}`} />
                    </span>
                  </button>
                  {openDropdown === link.labelKey && (
                    <div className="absolute top-full left-0 bg-white shadow-lg border border-gray-100 rounded-md py-2 min-w-[260px] z-50">
                      {link.children.map((child) => (
                        <Link
                          key={child.labelKey}
                          to={child.href}
                          className="block px-4 py-2 text-sm text-text hover:text-brandgreen hover:bg-surface transition-colors whitespace-nowrap"
                        >
                          {t(child.labelKey)}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={link.href}
                  className={`px-3 py-2 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${
                    location.pathname === link.href
                      ? "text-brandgreen"
                      : "text-text hover:text-brandgreen"
                  }`}
                >
                  {t(link.labelKey)}
                </Link>
              )}
            </div>
          ))}

          {/* Language switcher */}
          <div className="relative ml-2">
            <button
              onClick={() => setLangDropdownOpen(!langDropdownOpen)}
              className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-magenta border border-magenta rounded-full hover:bg-magenta hover:text-white transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-global-line" />
              </span>
              {currentLang}
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-down-s-line text-xs" />
              </span>
            </button>
            {langDropdownOpen && (
              <div className="absolute top-full right-0 mt-1 bg-white shadow-lg border border-gray-100 rounded-md py-1 min-w-[100px] z-50">
                <button
                  onClick={() => handleLanguageChange("es")}
                  className={`block w-full text-left px-4 py-2 text-sm hover:bg-surface transition-colors ${
                    i18n.language === "es" ? "text-brandgreen font-semibold" : "text-text"
                  }`}
                >
                  Espanol
                </button>
                <button
                  onClick={() => handleLanguageChange("en")}
                  className={`block w-full text-left px-4 py-2 text-sm hover:bg-surface transition-colors ${
                    i18n.language === "en" ? "text-brandgreen font-semibold" : "text-text"
                  }`}
                >
                  English
                </button>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile hamburger + lang */}
        <div className="lg:hidden flex items-center gap-2">
          <button
            onClick={() => setLangDropdownOpen(!langDropdownOpen)}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-magenta border border-magenta rounded-full hover:bg-magenta hover:text-white transition-colors whitespace-nowrap cursor-pointer"
          >
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-global-line" />
            </span>
            {currentLang}
          </button>
          <button
            className="w-10 h-10 flex items-center justify-center text-text cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <span className="w-6 h-6 flex items-center justify-center">
              {mobileOpen ? (
                <i className="ri-close-line text-xl" />
              ) : (
                <i className="ri-menu-line text-xl" />
              )}
            </span>
          </button>
        </div>
      </div>

      {/* Mobile lang dropdown */}
      {langDropdownOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-4 py-2">
          <button
            onClick={() => handleLanguageChange("es")}
            className={`block w-full text-left px-2 py-2 text-sm ${
              i18n.language === "es" ? "text-brandgreen font-semibold" : "text-text"
            }`}
          >
            Espanol
          </button>
          <button
            onClick={() => handleLanguageChange("en")}
            className={`block w-full text-left px-2 py-2 text-sm ${
              i18n.language === "en" ? "text-brandgreen font-semibold" : "text-text"
            }`}
          >
            English
          </button>
        </div>
      )}

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-4 pb-4">
          {navLinks.map((link) => (
            <div key={link.labelKey}>
              {link.children ? (
                <div>
                  <button
                    className="w-full text-left px-2 py-3 text-sm font-medium uppercase tracking-wide text-text border-b border-gray-100 flex items-center justify-between cursor-pointer"
                    onClick={() => setMobileDropdown(mobileDropdown === link.labelKey ? null : link.labelKey)}
                  >
                    {t(link.labelKey)}
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i
                        className={`ri-arrow-down-s-line text-sm transition-transform ${
                          mobileDropdown === link.labelKey ? "rotate-180" : ""
                        }`}
                      />
                    </span>
                  </button>
                  {mobileDropdown === link.labelKey && (
                    <div className="pl-4">
                      {link.children.map((child) => (
                        <Link
                          key={child.labelKey}
                          to={child.href}
                          className="block px-2 py-2 text-sm text-text hover:text-brandgreen transition-colors whitespace-nowrap"
                        >
                          {t(child.labelKey)}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={link.href}
                  className="block px-2 py-3 text-sm font-medium uppercase tracking-wide text-text hover:text-brandgreen transition-colors whitespace-nowrap border-b border-gray-100"
                >
                  {t(link.labelKey)}
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </header>
  );
}